// Placeholder data for Partner & Employee portals (training, certification, deal stats).
import {
  GraduationCap, BookOpen, Award, ShieldCheck, Sparkles, Cpu, Workflow,
  BarChart3, Layers, Handshake, Compass, Zap, Lock, Trophy,
} from 'lucide-react';

// ----- Training -----
export const trainingTracks = {
  partner: [
    {
      id: 'foundation',
      icon: BookOpen,
      tag: 'Foundation · 4 modules',
      title: 'MetaxuOne Platform Foundation',
      description: 'A guided introduction to Process Intelligence, the Context Model, and the MetaxuOne Platform architecture.',
      duration: '6 hrs',
      level: 'Beginner',
    },
    {
      id: 'pi-graph',
      icon: Workflow,
      tag: 'Specialist · 6 modules',
      title: 'Process Intelligence Graph Specialist',
      description: 'Design connected process models across ERP, CRM, ITSM and custom systems with real-world labs.',
      duration: '12 hrs',
      level: 'Intermediate',
    },
    {
      id: 'ai-agents',
      icon: Cpu,
      tag: 'Specialist · 5 modules',
      title: 'AI & Agents Builder',
      description: 'Build, deploy, and orchestrate AI agents that reason on top of the Context Model.',
      duration: '10 hrs',
      level: 'Intermediate',
    },
    {
      id: 'partner-sales',
      icon: Handshake,
      tag: 'Sales · 3 modules',
      title: 'Partner Sales Acceleration',
      description: 'Messaging, discovery questions, and ROI calculators to lead a winning customer conversation.',
      duration: '4 hrs',
      level: 'Beginner',
    },
    {
      id: 'solution-architect',
      icon: Layers,
      tag: 'Expert · 8 modules',
      title: 'Solution Architect Track',
      description: 'End-to-end blueprint to design enterprise-grade MetaxuOne deployments and integration patterns.',
      duration: '20 hrs',
      level: 'Advanced',
    },
    {
      id: 'admin',
      icon: ShieldCheck,
      tag: 'Operations · 4 modules',
      title: 'Platform Administrator',
      description: 'Tenant management, RBAC, observability and best practices for running MetaxuOne at scale.',
      duration: '8 hrs',
      level: 'Intermediate',
    },
  ],
  employee: [
    {
      id: 'onboarding',
      icon: Sparkles,
      tag: 'Onboarding · 5 modules',
      title: 'MetaxuOne Onboarding',
      description: 'Everything a new MetaxuOne team member needs in their first 30 days — product, processes, and people.',
      duration: '8 hrs',
      level: 'Beginner',
    },
    {
      id: 'product-deep-dive',
      icon: Workflow,
      tag: 'Product · 7 modules',
      title: 'Product Deep Dive',
      description: 'Hands-on walkthrough of every module of the MetaxuOne Platform with internal demos.',
      duration: '15 hrs',
      level: 'Intermediate',
    },
    {
      id: 'demo-mastery',
      icon: Zap,
      tag: 'Sales · 4 modules',
      title: 'Demo Mastery',
      description: 'Run an objection-proof live demo, including handling AI/governance questions.',
      duration: '6 hrs',
      level: 'Intermediate',
    },
    {
      id: 'security',
      icon: Lock,
      tag: 'Compliance · 3 modules',
      title: 'Security & Compliance Essentials',
      description: 'Required annual training on data privacy, secure coding, and customer data handling.',
      duration: '3 hrs',
      level: 'All',
    },
    {
      id: 'customer-success',
      icon: Compass,
      tag: 'Delivery · 6 modules',
      title: 'Customer Success Playbook',
      description: 'Playbooks for onboarding, value realization, and renewals — built on top of real engagements.',
      duration: '10 hrs',
      level: 'Intermediate',
    },
    {
      id: 'ai-deep',
      icon: Cpu,
      tag: 'Engineering · 5 modules',
      title: 'AI Engineering Deep Dive',
      description: 'Inside the Context Model and agent orchestration engine — for our R&D and SE teams.',
      duration: '14 hrs',
      level: 'Advanced',
    },
  ],
};

// ----- Certifications -----
export const certifications = {
  partner: [
    {
      id: 'maca',
      icon: Award,
      level: 'Associate',
      title: 'MetaxuOne Certified Associate (MaCA)',
      description: 'Foundational certification covering platform fundamentals and Process Intelligence basics.',
      duration: '60 min · 50 questions',
      requires: 'MetaxuOne Platform Foundation track',
      badge: 'bg-gradient-to-br from-blue-400 to-blue-600',
    },
    {
      id: 'mcps',
      icon: GraduationCap,
      level: 'Specialist',
      title: 'MetaxuOne Certified Process Specialist (MCPS)',
      description: 'Validates ability to design and deploy Process Intelligence models for customers.',
      duration: '90 min · 70 questions',
      requires: 'PI Graph Specialist + 1 customer project',
      badge: 'bg-gradient-to-br from-violet-400 to-indigo-600',
    },
    {
      id: 'mcaa',
      icon: Cpu,
      level: 'Specialist',
      title: 'MetaxuOne AI Agent Architect (MCAA)',
      description: 'Demonstrates expertise in designing AI agents on top of MetaxuOne\u2019s Context Model.',
      duration: '90 min · 70 questions',
      requires: 'AI & Agents Builder track',
      badge: 'bg-gradient-to-br from-cyan-400 to-blue-600',
    },
    {
      id: 'mcse',
      icon: Trophy,
      level: 'Expert',
      title: 'MetaxuOne Solution Expert (MCSE)',
      description: 'Top-tier credential for architects leading multi-stream enterprise transformations.',
      duration: '120 min · 90 questions + lab',
      requires: 'MCPS + Solution Architect track',
      badge: 'bg-gradient-to-br from-amber-400 to-orange-600',
    },
  ],
  employee: [
    {
      id: 'internal-maca',
      icon: Award,
      level: 'Associate',
      title: 'Internal MetaxuOne Associate',
      description: 'Required certification for every new joiner within the first 60 days.',
      duration: '45 min · 40 questions',
      requires: 'MetaxuOne Onboarding',
      badge: 'bg-gradient-to-br from-blue-400 to-blue-600',
    },
    {
      id: 'internal-product',
      icon: BarChart3,
      level: 'Specialist',
      title: 'Product Specialist (Internal)',
      description: 'Deep product knowledge across all modules and reference architectures.',
      duration: '90 min · 70 questions',
      requires: 'Product Deep Dive',
      badge: 'bg-gradient-to-br from-violet-400 to-indigo-600',
    },
    {
      id: 'internal-demo',
      icon: Zap,
      level: 'Specialist',
      title: 'Live Demo Certified',
      description: 'Required to lead customer-facing demos. Includes a recorded demo evaluation.',
      duration: '60 min + recorded demo',
      requires: 'Demo Mastery',
      badge: 'bg-gradient-to-br from-emerald-400 to-teal-600',
    },
    {
      id: 'internal-security',
      icon: Lock,
      level: 'Compliance',
      title: 'Security & Privacy Steward',
      description: 'Annual compliance certification for all MetaxuOne employees and contractors.',
      duration: '30 min · 30 questions',
      requires: 'Security & Compliance Essentials',
      badge: 'bg-gradient-to-br from-rose-400 to-red-600',
    },
  ],
};

// ----- Deal-registration stats (placeholder) -----
export const dealStats = [
  { value: '5–12%', label: 'Average margin uplift on registered deals' },
  { value: '< 2 days', label: 'Typical approval turnaround' },
  { value: '100%', label: 'Protected territory on approval' },
];
